import React, { useState, useEffect, useCallback } from 'react';
import {
    Snackbar, Alert,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    Button, Switch,
} from '@mui/material';
import gmailService from '../services/gmailService';
import outlookService from '../services/outlookService';
import PageHeader from '../components/ui/PageHeader';

const ROL_BADGE = {
    ADMIN:     { bg: '#fee2e2', color: '#991b1b' },
    ART:       { bg: '#ede9fe', color: '#4c1d95' },
    RTI:       { bg: '#f1f5f9', color: '#334155' },
    RECEPCION: { bg: '#dbeafe', color: '#1e40af' },
};

const formatFecha = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString('es-AR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

// ── Panel genérico reutilizado por Gmail y Outlook ─────────────────────────
function PanelCuentas({ proveedor, servicio, paramConectado, paramError, colorHeader }) {
    const [cuentas, setCuentas] = useState([]);
    const [loadingCuentas, setLoadingCuentas] = useState(true);
    const [consultando, setConsultando] = useState(false);

    // Dialog de suscriptores
    const [dialogCuenta, setDialogCuenta] = useState(null);
    const [suscripciones, setSuscripciones] = useState([]);
    const [loadingSuscripciones, setLoadingSuscripciones] = useState(false);

    // Dialog de filtros (por suscripción)
    // dialogFiltros = { idSuscripcion, idCredencial, nombreUsuario }
    const [dialogFiltros, setDialogFiltros] = useState(null);
    const [filtros, setFiltros] = useState([]);
    const [loadingFiltros, setLoadingFiltros] = useState(false);
    const [nuevoFiltroTipo, setNuevoFiltroTipo] = useState('REMITENTE');
    const [nuevoFiltroValor, setNuevoFiltroValor] = useState('');
    const [agregandoFiltro, setAgregandoFiltro] = useState(false);

    const [cuentaADesconectar, setCuentaADesconectar] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const mostrarSnackbar = (message, severity = 'success') =>
        setSnackbar({ open: true, message, severity });

    const fetchCuentas = useCallback(async () => {
        try {
            setLoadingCuentas(true);
            const res = await servicio.getCuentas();
            setCuentas(res.data);
        } catch {
            mostrarSnackbar(`Error al cargar las cuentas de ${proveedor}`, 'error');
        } finally {
            setLoadingCuentas(false);
        }
    }, [servicio, proveedor]);

    useEffect(() => { fetchCuentas(); }, [fetchCuentas]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get(paramConectado) === 'true') {
            mostrarSnackbar(`¡Cuenta de ${proveedor} conectada correctamente!`, 'success');
            fetchCuentas();
            window.history.replaceState({}, '', window.location.pathname);
        } else if (params.get(paramConectado) === 'false') {
            const err = params.get(paramError);
            mostrarSnackbar(`No se pudo conectar la cuenta de ${proveedor}${err ? ': ' + err : ''}`, 'error');
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, [fetchCuentas, proveedor, paramConectado, paramError]);

    const handleConectar = async () => {
        try {
            const res = await servicio.getAuthUrl();
            window.location.href = res.data.url;
        } catch (err) {
            mostrarSnackbar(err.response?.data?.error || `Error al generar el enlace de ${proveedor}`, 'error');
        }
    };

    const handleDesconectar = async () => {
        if (!cuentaADesconectar) return;
        const id = cuentaADesconectar.idCredencial;
        setCuentaADesconectar(null);
        try {
            await servicio.desconectarCuenta(id);
            mostrarSnackbar('Cuenta desconectada correctamente');
            setCuentas(prev => prev.filter(c => c.idCredencial !== id));
            if (dialogCuenta?.idCredencial === id) setDialogCuenta(null);
        } catch {
            mostrarSnackbar('Error al desconectar la cuenta', 'error');
        }
    };

    const handleConsultarAhora = async () => {
        setConsultando(true);
        try {
            await servicio.consultarAhora();
            mostrarSnackbar('Consulta ejecutada — revisá el panel de notificaciones');
            await fetchCuentas();
        } catch (err) {
            mostrarSnackbar(err.response?.data?.error || `Error al consultar ${proveedor}`, 'error');
        } finally {
            setConsultando(false);
        }
    };

    const handleAbrirSuscriptores = async (cuenta) => {
        setDialogCuenta(cuenta);
        setLoadingSuscripciones(true);
        setSuscripciones([]);
        try {
            const res = await servicio.getSuscripciones(cuenta.idCredencial);
            setSuscripciones(res.data);
        } catch {
            mostrarSnackbar('Error al cargar los suscriptores', 'error');
        } finally {
            setLoadingSuscripciones(false);
        }
    };

    const handleToggle = async (usuario) => {
        const idCredencial = dialogCuenta.idCredencial;
        try {
            if (usuario.suscripto) {
                await servicio.desuscribir(idCredencial, usuario.idUsuario);
                mostrarSnackbar(`${usuario.nombre} dejará de recibir notificaciones de esta cuenta`);
            } else {
                await servicio.suscribir(idCredencial, usuario.idUsuario);
                mostrarSnackbar(`${usuario.nombre} recibirá notificaciones de esta cuenta`);
            }
            // Recargar suscripciones para obtener el idSuscripcion actualizado
            const res = await servicio.getSuscripciones(idCredencial);
            setSuscripciones(res.data);
            const delta = usuario.suscripto ? -1 : 1;
            setCuentas(prev =>
                prev.map(c => c.idCredencial === idCredencial
                    ? { ...c, totalSuscriptores: c.totalSuscriptores + delta } : c)
            );
        } catch {
            mostrarSnackbar('Error al actualizar la suscripción', 'error');
        }
    };

    // Abre el dialog de filtros para una suscripción específica
    const handleAbrirFiltros = async (suscriptor) => {
        setDialogFiltros({
            idSuscripcion: suscriptor.idSuscripcion,
            idCredencial: dialogCuenta.idCredencial,
            nombreUsuario: suscriptor.nombre,
        });
        setLoadingFiltros(true);
        setFiltros([]);
        setNuevoFiltroValor('');
        setNuevoFiltroTipo('REMITENTE');
        try {
            const res = await servicio.getFiltrosSuscripcion(dialogCuenta.idCredencial, suscriptor.idSuscripcion);
            setFiltros(res.data);
        } catch {
            mostrarSnackbar('Error al cargar los filtros', 'error');
        } finally {
            setLoadingFiltros(false);
        }
    };

    const handleAgregarFiltro = async () => {
        if (!nuevoFiltroValor.trim() || !dialogFiltros) return;
        setAgregandoFiltro(true);
        try {
            const res = await servicio.agregarFiltroSuscripcion(
                dialogFiltros.idCredencial,
                dialogFiltros.idSuscripcion,
                nuevoFiltroTipo,
                nuevoFiltroValor.trim()
            );
            setFiltros(prev => [...prev, res.data]);
            setNuevoFiltroValor('');
            mostrarSnackbar('Filtro agregado correctamente');
        } catch (err) {
            mostrarSnackbar(err.response?.data?.error || 'Error al agregar el filtro', 'error');
        } finally {
            setAgregandoFiltro(false);
        }
    };

    const handleEliminarFiltro = async (idFiltro) => {
        if (!dialogFiltros) return;
        try {
            await servicio.eliminarFiltroSuscripcion(
                dialogFiltros.idCredencial,
                dialogFiltros.idSuscripcion,
                idFiltro
            );
            setFiltros(prev => prev.filter(f => f.idFiltro !== idFiltro));
            mostrarSnackbar('Filtro eliminado');
        } catch {
            mostrarSnackbar('Error al eliminar el filtro', 'error');
        }
    };

    const esGmail = proveedor === 'Gmail';
    const iconoProveedor = esGmail ? 'bi-google' : 'bi-microsoft';

    return (
        <>
            <div className="card shadow-sm" style={{ borderRadius: 12, border: 'none' }}>
                <div
                    className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2"
                    style={{
                        background: colorHeader,
                        borderRadius: '12px 12px 0 0',
                        padding: 'clamp(0.75rem, 2vw, 1.25rem) clamp(1rem, 3vw, 1.5rem)',
                    }}
                >
                    <div className="d-flex align-items-center gap-2">
                        <i className={`bi ${iconoProveedor} text-white fs-5`} />
                        <h5 className="mb-0 text-white fw-semibold" style={{ fontSize: 'clamp(1rem, 2.5vw, 1.2rem)' }}>
                            Integración {proveedor}
                        </h5>
                    </div>
                    <div className="d-flex gap-2 flex-wrap">
                        {cuentas.length > 0 && (
                            <button
                                className="btn btn-sm"
                                onClick={handleConsultarAhora}
                                disabled={consultando}
                                style={{
                                    background: 'rgba(255,255,255,0.15)', color: '#fff',
                                    border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8, fontSize: '0.82rem',
                                }}
                            >
                                {consultando
                                    ? <><i className="bi bi-arrow-repeat me-1" style={{ animation: 'spin 1s linear infinite' }} />Consultando...</>
                                    : <><i className="bi bi-lightning-fill me-1" />Consultar ahora</>
                                }
                            </button>
                        )}
                        <button
                            className="btn btn-sm"
                            onClick={handleConectar}
                            style={{
                                background: '#fff', color: '#2563eb',
                                border: 'none', borderRadius: 8, fontWeight: 600, fontSize: '0.82rem',
                            }}
                        >
                            <i className="bi bi-plus-circle-fill me-1" />
                            Conectar cuenta {proveedor}
                        </button>
                    </div>
                </div>

                <div className="card-body p-0">
                    {loadingCuentas ? (
                        <p className="text-muted p-4 mb-0">Cargando cuentas...</p>
                    ) : cuentas.length === 0 ? (
                        <div className="text-center py-5 px-3">
                            <i className="bi bi-envelope-slash" style={{ fontSize: '2.5rem', color: '#cbd5e1' }} />
                            <p className="mt-3 mb-1 fw-semibold" style={{ color: '#64748b' }}>
                                No hay cuentas de {proveedor} conectadas
                            </p>
                            <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                                Hacé clic en <strong>Conectar cuenta {proveedor}</strong> para comenzar.
                            </p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover mb-0" style={{ fontSize: 'clamp(0.82rem, 2vw, 0.95rem)' }}>
                                <thead style={{ background: '#f8fafc' }}>
                                    <tr>
                                        <th className="px-4 py-3 fw-semibold" style={{ color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>Cuenta</th>
                                        <th className="px-3 py-3 fw-semibold text-center" style={{ color: '#64748b', borderBottom: '1px solid #e2e8f0', width: 130 }}>Suscriptores</th>
                                        <th className="px-3 py-3 fw-semibold" style={{ color: '#64748b', borderBottom: '1px solid #e2e8f0', width: 170 }}>Última consulta</th>
                                        <th className="px-3 py-3 fw-semibold text-center" style={{ color: '#64748b', borderBottom: '1px solid #e2e8f0', width: 150 }}>Acciones</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cuentas.map(cuenta => (
                                        <tr key={cuenta.idCredencial}>
                                            <td className="px-4 py-3 align-middle">
                                                <div className="d-flex align-items-center gap-2">
                                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10B981', flexShrink: 0 }} />
                                                    <span className="fw-semibold" style={{ color: '#1e293b' }}>{cuenta.emailCuenta}</span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-3 align-middle text-center">
                                                <span className="badge" style={{
                                                    background: cuenta.totalSuscriptores > 0 ? '#dbeafe' : '#f1f5f9',
                                                    color: cuenta.totalSuscriptores > 0 ? '#1e40af' : '#64748b',
                                                    fontWeight: 600, padding: '4px 10px', borderRadius: 6,
                                                }}>
                                                    {cuenta.totalSuscriptores} activo{cuenta.totalSuscriptores !== 1 ? 's' : ''}
                                                </span>
                                            </td>
                                            <td className="px-3 py-3 align-middle" style={{ color: '#64748b', fontSize: '0.82rem' }}>
                                                {formatFecha(cuenta.ultimaConsulta)}
                                            </td>
                                            <td className="px-3 py-3 align-middle text-center">
                                                <div className="d-flex gap-2 justify-content-center">
                                                    <button className="btn btn-sm btn-outline-primary" onClick={() => handleAbrirSuscriptores(cuenta)} style={{ borderRadius: 8, fontSize: '0.8rem' }} title="Gestionar suscriptores">
                                                        <i className="bi bi-people-fill me-1" />Suscriptores
                                                    </button>
                                                    <button className="btn btn-sm btn-outline-danger" onClick={() => setCuentaADesconectar(cuenta)} style={{ borderRadius: 8, fontSize: '0.8rem' }} title="Desconectar">
                                                        <i className="bi bi-plug-fill" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <div className="px-4 py-3" style={{ borderTop: cuentas.length > 0 ? '1px solid #e2e8f0' : 'none', background: '#f8fafc', borderRadius: '0 0 12px 12px' }}>
                        <p className="mb-0 text-muted" style={{ fontSize: '0.8rem' }}>
                            <i className="bi bi-info-circle me-1" />
                            Podés conectar varias cuentas de {proveedor} distintas. Cada suscriptor configura sus propios filtros.
                            El scheduler consulta automáticamente cada 30 min de lunes a viernes entre 8:00 y 17:30.
                        </p>
                    </div>
                </div>
            </div>

            {/* ── Dialog: suscriptores ─────────────────────────────────── */}
            <Dialog open={!!dialogCuenta} onClose={() => setDialogCuenta(null)} maxWidth="md" fullWidth PaperProps={{ style: { borderRadius: 12 } }}>
                <DialogTitle style={{ background: colorHeader, color: '#fff', borderRadius: '12px 12px 0 0' }}>
                    <i className="bi bi-people-fill me-2" />
                    Suscriptores de <span style={{ fontWeight: 700 }}>{dialogCuenta?.emailCuenta}</span>
                </DialogTitle>
                <DialogContent dividers style={{ padding: 0 }}>
                    {loadingSuscripciones ? (
                        <p className="text-muted p-4 mb-0">Cargando usuarios...</p>
                    ) : suscripciones.length === 0 ? (
                        <p className="text-muted p-4 mb-0">No hay usuarios validados.</p>
                    ) : (
                        <table className="table table-hover mb-0" style={{ fontSize: '0.9rem' }}>
                            <thead style={{ background: '#f8fafc' }}>
                                <tr>
                                    <th className="px-4 py-3 fw-semibold" style={{ color: '#64748b', border: 'none' }}>Usuario</th>
                                    <th className="px-3 py-3 fw-semibold" style={{ color: '#64748b', border: 'none' }}>Rol</th>
                                    <th className="px-3 py-3 fw-semibold text-center" style={{ color: '#64748b', border: 'none', width: 80 }}>Activo</th>
                                    <th className="px-3 py-3 fw-semibold text-center" style={{ color: '#64748b', border: 'none', width: 100 }}>Filtros</th>
                                </tr>
                            </thead>
                            <tbody>
                                {suscripciones.map(u => {
                                    const badge = ROL_BADGE[u.rol?.toUpperCase()] || { bg: '#f1f5f9', color: '#334155' };
                                    return (
                                        <tr key={u.idUsuario}>
                                            <td className="px-4 py-3 align-middle">
                                                <div className="fw-semibold" style={{ color: '#1e293b' }}>{u.nombre || '(sin nombre)'}</div>
                                                <div style={{ color: '#94a3b8', fontSize: '0.78rem' }}>{u.email}</div>
                                            </td>
                                            <td className="px-3 py-3 align-middle">
                                                <span className="badge" style={{ background: badge.bg, color: badge.color, fontWeight: 600, fontSize: '0.75rem', padding: '3px 9px', borderRadius: 6 }}>
                                                    {u.rol || '—'}
                                                </span>
                                            </td>
                                            <td className="px-3 py-3 align-middle text-center">
                                                <Switch checked={u.suscripto} onChange={() => handleToggle(u)} color="primary" size="small" />
                                            </td>
                                            <td className="px-3 py-3 align-middle text-center">
                                                {u.idSuscripcion ? (
                                                    <button
                                                        className="btn btn-sm btn-outline-warning"
                                                        onClick={() => handleAbrirFiltros(u)}
                                                        style={{ borderRadius: 8, fontSize: '0.75rem' }}
                                                        title="Configurar filtros de este suscriptor"
                                                    >
                                                        <i className="bi bi-funnel-fill" />
                                                    </button>
                                                ) : (
                                                    <span className="text-muted" style={{ fontSize: '0.75rem' }}>—</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: '12px 20px' }}>
                    <Button onClick={() => setDialogCuenta(null)} variant="contained" disableElevation style={{ borderRadius: 8 }}>Cerrar</Button>
                </DialogActions>
            </Dialog>

            {/* ── Dialog: filtros de suscriptor ───────────────────────── */}
            <Dialog open={!!dialogFiltros} onClose={() => setDialogFiltros(null)} maxWidth="sm" fullWidth PaperProps={{ style: { borderRadius: 12 } }}>
                <DialogTitle style={{ background: 'var(--gradient-warning)', color: '#fff', borderRadius: '12px 12px 0 0' }}>
                    <i className="bi bi-funnel-fill me-2" />
                    Filtros de <span style={{ fontWeight: 700 }}>{dialogFiltros?.nombreUsuario}</span>
                </DialogTitle>
                <DialogContent dividers style={{ padding: '20px 24px' }}>
                    <p className="small text-muted mb-4" style={{ lineHeight: 1.5 }}>
                        Solo se notificarán los mails que coincidan con al menos uno de estos filtros.
                        Si no hay filtros configurados, se notificarán <strong>todos</strong> los mails de la cuenta.
                    </p>
                    {loadingFiltros ? (
                        <p className="text-muted">Cargando filtros...</p>
                    ) : (
                        <>
                            {/* Remitentes */}
                            <div className="mb-4">
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <span className="badge" style={{ background: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: '0.75rem', padding: '4px 10px', borderRadius: 6 }}>REMITENTE</span>
                                    <small className="text-muted">Filtro por dirección o dominio (ej: srt@srt.gob.ar, @mapfre.com.ar)</small>
                                </div>
                                {filtros.filter(f => f.tipo === 'REMITENTE').length === 0 ? (
                                    <p className="text-muted small fst-italic mb-2">Sin filtros de remitente</p>
                                ) : (
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        {filtros.filter(f => f.tipo === 'REMITENTE').map(f => (
                                            <span key={f.idFiltro} className="badge d-flex align-items-center gap-1"
                                                style={{ background: '#fff7ed', color: '#9a3412', border: '1px solid #fed7aa', fontSize: '0.82rem', padding: '5px 10px', borderRadius: 8 }}>
                                                <i className="bi bi-at" />{f.valor}
                                                <button onClick={() => handleEliminarFiltro(f.idFiltro)}
                                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#9a3412', marginLeft: 2, lineHeight: 1 }}>
                                                    <i className="bi bi-x-lg" style={{ fontSize: '0.7rem' }} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {/* Asuntos */}
                            <div className="mb-4">
                                <div className="d-flex align-items-center gap-2 mb-2">
                                    <span className="badge" style={{ background: '#ede9fe', color: '#4c1d95', fontWeight: 700, fontSize: '0.75rem', padding: '4px 10px', borderRadius: 6 }}>ASUNTO</span>
                                    <small className="text-muted">Filtro por palabra o frase en el asunto (ej: expediente, CUIL)</small>
                                </div>
                                {filtros.filter(f => f.tipo === 'ASUNTO').length === 0 ? (
                                    <p className="text-muted small fst-italic mb-2">Sin filtros de asunto</p>
                                ) : (
                                    <div className="d-flex flex-wrap gap-2 mb-2">
                                        {filtros.filter(f => f.tipo === 'ASUNTO').map(f => (
                                            <span key={f.idFiltro} className="badge d-flex align-items-center gap-1"
                                                style={{ background: '#f5f3ff', color: '#4c1d95', border: '1px solid #ddd6fe', fontSize: '0.82rem', padding: '5px 10px', borderRadius: 8 }}>
                                                <i className="bi bi-chat-quote" />{f.valor}
                                                <button onClick={() => handleEliminarFiltro(f.idFiltro)}
                                                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#4c1d95', marginLeft: 2, lineHeight: 1 }}>
                                                    <i className="bi bi-x-lg" style={{ fontSize: '0.7rem' }} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {/* Agregar filtro */}
                            <div className="rounded-3 p-3" style={{ background: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                <p className="fw-semibold small mb-2" style={{ color: '#475569' }}>
                                    <i className="bi bi-plus-circle me-1" />Agregar filtro
                                </p>
                                <div className="d-flex gap-2 align-items-end">
                                    <div>
                                        <label className="form-label small text-muted mb-1">Tipo</label>
                                        <select className="form-select form-select-sm" value={nuevoFiltroTipo} onChange={e => setNuevoFiltroTipo(e.target.value)} style={{ borderRadius: 8, width: 130 }}>
                                            <option value="REMITENTE">Remitente</option>
                                            <option value="ASUNTO">Asunto</option>
                                        </select>
                                    </div>
                                    <div className="flex-grow-1">
                                        <label className="form-label small text-muted mb-1">Valor</label>
                                        <input type="text" className="form-control form-control-sm"
                                            placeholder={nuevoFiltroTipo === 'REMITENTE' ? 'ej: srt@srt.gob.ar' : 'ej: expediente'}
                                            value={nuevoFiltroValor} onChange={e => setNuevoFiltroValor(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') handleAgregarFiltro(); }}
                                            style={{ borderRadius: 8 }} />
                                    </div>
                                    <button className="btn btn-sm btn-warning fw-semibold" onClick={handleAgregarFiltro}
                                        disabled={agregandoFiltro || !nuevoFiltroValor.trim()} style={{ borderRadius: 8, whiteSpace: 'nowrap' }}>
                                        {agregandoFiltro
                                            ? <i className="bi bi-arrow-repeat" style={{ animation: 'spin 1s linear infinite' }} />
                                            : <><i className="bi bi-plus me-1" />Agregar</>
                                        }
                                    </button>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
                <DialogActions style={{ padding: '12px 20px' }}>
                    <Button onClick={() => setDialogFiltros(null)} variant="contained" disableElevation style={{ borderRadius: 8 }}>Cerrar</Button>
                </DialogActions>
            </Dialog>

            {/* ── Dialog: confirmar desconexión ────────────────────────── */}
            <Dialog open={!!cuentaADesconectar} onClose={() => setCuentaADesconectar(null)} maxWidth="xs">
                <DialogTitle>¿Desconectar esta cuenta?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Se eliminarán las credenciales OAuth de <strong>{cuentaADesconectar?.emailCuenta}</strong> y todas sus suscripciones asociadas.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCuentaADesconectar(null)}>Cancelar</Button>
                    <Button onClick={handleDesconectar} color="error" variant="contained">Desconectar</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}

// ── Página principal con tabs ──────────────────────────────────────────────
export default function AdminEmailPage() {
    const [tabActiva, setTabActiva] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.has('outlookConectado') || params.has('outlookError')) return 'outlook';
        return 'gmail';
    });

    return (
        <div className="area-admin">
            <div style={{ padding: 'clamp(1rem, 3vw, 2rem)', maxWidth: 960, margin: '0 auto' }}>
                <PageHeader
                    variante="plano"
                    icono="envelope-at-fill"
                    titulo="Integraciones de Correo"
                    descripcion="Administración de cuentas sincronizadas de Gmail y Outlook / Hotmail"
                />

                {/* Tabs */}
                <div className="d-flex gap-2 mb-4">
                <button
                    className="btn btn-sm fw-semibold d-flex align-items-center gap-2"
                    onClick={() => setTabActiva('gmail')}
                    style={{
                        borderRadius: 10,
                        padding: '0.5rem 1.2rem',
                        background: tabActiva === 'gmail' ? 'var(--primary)' : '#f1f5f9',
                        color: tabActiva === 'gmail' ? '#fff' : '#64748b',
                        border: 'none',
                        transition: 'all 0.2s',
                    }}
                >
                    <i className="bi bi-google" style={{ color: tabActiva === 'gmail' ? '#fff' : '#EA4335' }} />
                    Gmail
                </button>
                <button
                    className="btn btn-sm fw-semibold d-flex align-items-center gap-2"
                    onClick={() => setTabActiva('outlook')}
                    style={{
                        borderRadius: 10,
                        padding: '0.5rem 1.2rem',
                        background: tabActiva === 'outlook' ? 'var(--info-text)' : '#f1f5f9',
                        color: tabActiva === 'outlook' ? '#fff' : '#64748b',
                        border: 'none',
                        transition: 'all 0.2s',
                    }}
                >
                    <i className="bi bi-microsoft" style={{ color: tabActiva === 'outlook' ? '#fff' : '#0078D4' }} />
                    Outlook / Hotmail
                </button>
            </div>

            {tabActiva === 'gmail' && (
                <PanelCuentas
                    proveedor="Gmail"
                    servicio={gmailService}
                    paramConectado="gmailConectado"
                    paramError="gmailError"
                    colorHeader="var(--primary)"
                />
            )}

            {tabActiva === 'outlook' && (
                <PanelCuentas
                    proveedor="Outlook"
                    servicio={outlookService}
                    paramConectado="outlookConectado"
                    paramError="outlookError"
                    colorHeader="var(--info-text)"
                />
            )}

            <style>{`
                @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
            `}</style>
            </div>
        </div>
    );
}
